import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PageLayout, PageHeader, Section } from "../../ui/Layout";
import {
  createKeyword,
  getCompanyInfo,
  listOfLanguages,
  listOfAnalysts,
  defaultPrompt,
} from "../../../services/NewsService";

const CreateKeyword = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    tag: "",
    language: "English",
    target_ticker: "",
    buyer_ticker: "",
    company_keywords: "",
    special_queries: "",
    company_prompt: "",
    company_description: "",
    additional_prompt: "",
    override_prompt: "",
    default_prompt: "",
    whitelist: "",
    blacklist: "",
    status: "Active",
    analysts: [],
  });

  const [loading, setLoading] = useState(false);
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [companyInfoSuccess, setCompanyInfoSuccess] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [overridePromptPassword, setOverridePromptPassword] = useState("");
  const [overridePromptUnlocked, setOverridePromptUnlocked] = useState(false);
  const [systemDefaultPrompt, setSystemDefaultPrompt] = useState("");

  // Load default prompt on component mount
  useEffect(() => {
    const fetchDefaultPrompt = async () => {
      try {
        const data = await defaultPrompt();
        // Handle different possible response structures
        const promptText =
          data?.default_prompt || data?.defaultPrompt || data || "";
        setSystemDefaultPrompt(promptText);
      } catch (err) {
        console.error("Error fetching default prompt:", err);
        // Keep placeholder if fetch fails
        setSystemDefaultPrompt(
          "Default prompt for news analysis (managed by system)"
        );
      }
    };

    fetchDefaultPrompt();
  }, []);

  // Load languages on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      setLanguagesLoading(true);
      try {
        const data = await listOfLanguages();
        setLanguages(data.languages || []);
      } catch (err) {
        console.error("Error fetching languages:", err);
        setError("Failed to load languages. Please try again.");
      } finally {
        setLanguagesLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  // Load analysts on component mount
  useEffect(() => {
    const fetchAnalysts = async () => {
      setAnalystsLoading(true);
      try {
        const data = await listOfAnalysts();
        // Handle both array of objects or array of strings
        const analystsList = data.analysts || [];
        setAnalysts(analystsList);
      } catch (err) {
        console.error("Error fetching analysts:", err);
        setError("Failed to load analysts. Please try again.");
      } finally {
        setAnalystsLoading(false);
      }
    };

    fetchAnalysts();
  }, []);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    const newFormData = {
      ...formData,
      [field]: value,
    };

    // Auto-generate company_prompt when relevant fields change
    if (
      [
        "company_description",
        "whitelist",
        "blacklist",
        "additional_prompt",
      ].includes(field)
    ) {
      newFormData.company_prompt = generateCompanyPrompt(newFormData);
    }

    // Update default_prompt display based on override_prompt logic
    if (field === "override_prompt") {
      newFormData.default_prompt = computeDefaultPrompt(
        newFormData.override_prompt
      );
    }

    setFormData(newFormData);
  };

  const computeDefaultPrompt = (overridePrompt) => {
    // Logic from main.py lines 556-576
    // If override_prompt exists and is not empty, use it
    // Otherwise, use system default from backend
    if (overridePrompt && overridePrompt.trim() !== "") {
      return overridePrompt;
    }
    // Return system default from backend or placeholder if not loaded yet
    return (
      systemDefaultPrompt ||
      "Default prompt for news analysis (managed by system)"
    );
  };

  const computeProvidedPrompt = (overridePrompt, companyPrompt) => {
    // Logic from main.py lines 556-566
    // provided_prompt = f"{default_prompt}\n{company_result.company_prompt}"
    const defaultPrompt = computeDefaultPrompt(overridePrompt);
    if (companyPrompt && companyPrompt.trim() !== "") {
      return `${defaultPrompt}\n${companyPrompt}`;
    }
    return defaultPrompt;
  };

  const handleOverridePromptUnlock = () => {
    if (overridePromptPassword === "Glazer123") {
      setOverridePromptUnlocked(true);
      setOverridePromptPassword("");
    } else {
      setError("Incorrect password. Please try again.");
      setOverridePromptPassword("");
    }
  };

  const generateCompanyPrompt = (data) => {
    const PROMPT = "Analyze the following headlines for risk implications:";
    let sentence = "";

    // Add company description
    if (data.company_description) {
      sentence += `${data.company_description} `;
    }

    // Add whitelist and blacklist
    if (data.whitelist) {
      sentence += `Any headlines on ${data.whitelist} are always considered relevant (true). `;
    }
    if (data.blacklist) {
      sentence += `Any headlines on ${data.blacklist} are always considered irrelevant (false). `;
    }

    // Add additional prompt at the end
    if (data.additional_prompt) {
      sentence += `${data.additional_prompt} `;
    }

    if (sentence) {
      return `${PROMPT}\n${sentence} Classify the headlines as True for any headlines implying risk for the above company and False for any headlines not implying risk.`;
    } else {
      return "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await createKeyword(formData);
      setSuccess(true);
      setTimeout(() => {
        navigate("/MAB_News", {
          state: {
            activeTab: "tab2",
            filterValue: "",
          },
        });
      }, 1500);
    } catch (err) {
      console.error("Error creating keyword:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to create keyword. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/MAB_News", {
      state: {
        activeTab: "tab2",
        filterValue: "",
      },
    });
  };

  const handleGetCompanyInfo = async () => {
    if (!formData.target_ticker) {
      setError("Please enter a Target ticker before getting company info.");
      return;
    }

    setCompanyInfoLoading(true);
    setError(null);

    try {
      const companyInfo = await getCompanyInfo(
        formData.target_ticker,
        formData.buyer_ticker
      );

      // Update form data with company information
      const updatedFormData = { ...formData };

      // Combine company names for company keywords
      const companyNames = [];
      if (companyInfo.target_company?.name) {
        companyNames.push(companyInfo.target_company.name);
      }
      if (companyInfo.buyer_company?.name) {
        companyNames.push(companyInfo.buyer_company.name);
      }

      if (companyNames.length > 0) {
        // Append to existing company keywords instead of replacing
        const existingKeywords = formData.company_keywords
          ? formData.company_keywords.trim()
          : "";
        const newKeywords = companyNames.join(", ");

        if (existingKeywords) {
          updatedFormData.company_keywords = `${existingKeywords}, ${newKeywords}`;
        } else {
          updatedFormData.company_keywords = newKeywords;
        }
      }

      // Use target company description for company description
      if (companyInfo.target_company?.description) {
        updatedFormData.company_description =
          companyInfo.target_company.description;
      }

      // Generate company_prompt after updating company description
      updatedFormData.company_prompt = generateCompanyPrompt(updatedFormData);

      setFormData(updatedFormData);
      setCompanyInfoSuccess(true);
      setTimeout(() => setCompanyInfoSuccess(false), 3000);
    } catch (err) {
      console.error("Error getting company info:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to get company information. Please check the target ticker symbol and try again."
      );
    } finally {
      setCompanyInfoLoading(false);
    }
  };

  const shortFields = [
    {
      name: "tag",
      label: "Tag",
      placeholder: "e.g., K/MARS",
      required: true,
    },
    {
      name: "language",
      label: "Language",
      type: "select",
      options: languagesLoading
        ? [{ value: "", label: "Loading languages..." }]
        : languages.map((lang) => ({
            value: lang.name,
            label: lang.name,
          })),
      required: true,
      helperText: "Language for news analysis",
      disabled: languagesLoading,
    },
    {
      name: "target_ticker",
      label: "Target Ticker",
      placeholder: "e.g., K",
      required: true,
    },
    {
      name: "buyer_ticker",
      label: "Buyer Ticker",
      placeholder: "e.g., MARS",
      required: true,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
      ],
      required: true,
      helperText: "Status of the keyword entry",
    },
  ];

  return (
    <PageLayout>
      <PageHeader
        title="Create Keyword Entry"
        subtitle="Add a new keyword set for news analysis"
      />

      {/* Action Buttons at Top */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-start",
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
          disabled={loading}
          sx={{
            borderColor: theme.palette.grey[400],
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.grey[600],
              backgroundColor: theme.palette.grey[50],
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={
            companyInfoLoading ? (
              <CircularProgress size={20} />
            ) : (
              <BusinessIcon />
            )
          }
          onClick={handleGetCompanyInfo}
          disabled={loading || companyInfoLoading}
          sx={{
            backgroundColor: theme.palette.info.main,
            "&:hover": {
              backgroundColor: theme.palette.info.dark,
            },
          }}
        >
          {companyInfoLoading ? "Getting Info..." : "Get Company Info"}
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
          onClick={handleSubmit}
          sx={{
            backgroundColor: theme.palette.success.main,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
            },
          }}
        >
          {loading ? "Creating..." : "Create Keyword"}
        </Button>
      </Box>

      <Section
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {/* Two-Column Layout */}
            <Grid container spacing={1.5}>
              {/* Left Column - Basic Info & Keywords */}
              <Grid item xs={12} lg={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    height: "100%",
                  }}
                >
                  {/* Basic Information Card */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      border: `2px solid ${theme.palette.grey[800]}`,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: `0 2px 4px ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: "0.95rem",
                      }}
                    >
                      Basic Information
                    </Typography>
                    <Grid container spacing={1}>
                      {shortFields.map((field) => (
                        <Grid item xs={12} sm={6} key={field.name}>
                          {field.type === "select" ? (
                            <FormControl
                              variant="outlined"
                              required={field.required}
                              fullWidth
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor:
                                    theme.palette.background.default,
                                },
                              }}
                            >
                              <InputLabel>{field.label}</InputLabel>
                              <Select
                                value={formData[field.name]}
                                onChange={handleInputChange(field.name)}
                                label={field.label}
                                disabled={field.disabled}
                              >
                                {field.options.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              name={field.name}
                              label={field.label}
                              placeholder={field.placeholder}
                              value={formData[field.name]}
                              onChange={handleInputChange(field.name)}
                              required={field.required}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor:
                                    theme.palette.background.default,
                                },
                              }}
                            />
                          )}
                        </Grid>
                      ))}
                      {/* Analysts Multi-Select Field */}
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          variant="outlined"
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        >
                          <InputLabel>Analysts</InputLabel>
                          <Select
                            multiple
                            value={formData.analysts || []}
                            onChange={handleInputChange("analysts")}
                            label="Analysts"
                            disabled={analystsLoading}
                            renderValue={(selected) => {
                              if (selected.length === 0) return "";
                              return selected.join(", ");
                            }}
                          >
                            {analystsLoading ? (
                              <MenuItem disabled>Loading analysts...</MenuItem>
                            ) : analysts.length === 0 ? (
                              <MenuItem disabled>
                                No analysts available
                              </MenuItem>
                            ) : (
                              analysts.map((analyst) => {
                                const analystName =
                                  typeof analyst === "object"
                                    ? analyst.name
                                    : analyst;
                                const analystValue =
                                  typeof analyst === "object"
                                    ? analyst.name
                                    : analyst;
                                const analystKey =
                                  typeof analyst === "object"
                                    ? analyst.id || analyst.name
                                    : analyst;
                                const isSelected = (
                                  formData.analysts || []
                                ).includes(analystValue);
                                return (
                                  <MenuItem
                                    key={analystKey}
                                    value={analystValue}
                                  >
                                    <Checkbox checked={isSelected} />
                                    <ListItemText primary={analystName} />
                                  </MenuItem>
                                );
                              })
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Keywords & Queries Card */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      border: `2px solid ${theme.palette.grey[800]}`,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.background.paper,
                      flex: 1,
                      boxShadow: `0 2px 4px ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: "0.95rem",
                      }}
                    >
                      Keywords & Queries
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          name="company_keywords"
                          label="Company Keywords"
                          placeholder="e.g., Kellogg, cereal, snacks, food"
                          value={formData.company_keywords}
                          onChange={handleInputChange("company_keywords")}
                          multiline
                          rows={2}
                          helperText="Company-specific keywords separated by commas"
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="special_queries"
                          label="Special Queries"
                          placeholder="e.g., merger, acquisition, deal"
                          value={formData.special_queries}
                          onChange={handleInputChange("special_queries")}
                          multiline
                          rows={2}
                          helperText="Special queries for news analysis"
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="company_description"
                          label="Company Description"
                          placeholder="Brief description of the company..."
                          value={formData.company_description}
                          onChange={handleInputChange("company_description")}
                          multiline
                          rows={2}
                          helperText="Brief description of the company for context"
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="whitelist"
                          label="Whitelist"
                          placeholder="e.g., merger news, acquisition updates"
                          value={formData.whitelist}
                          onChange={handleInputChange("whitelist")}
                          multiline
                          rows={2}
                          helperText="Always relevant phrases (comma-separated)"
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="blacklist"
                          label="Blacklist"
                          placeholder="e.g., sports news, entertainment"
                          value={formData.blacklist}
                          onChange={handleInputChange("blacklist")}
                          multiline
                          rows={2}
                          helperText="Always irrelevant phrases (comma-separated)"
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: theme.palette.background.default,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              </Grid>

              {/* Right Column - Prompts */}
              <Grid item xs={12} lg={6}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    border: `2px solid ${theme.palette.grey[800]}`,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.background.paper,
                    height: "100%",
                    boxShadow: `0 2px 4px ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: "0.95rem",
                    }}
                  >
                    Analysis Prompts
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        name="company_prompt"
                        label="Company Prompt (Auto-generated)"
                        placeholder="Will be generated based on company description, whitelist, and blacklist..."
                        value={formData.company_prompt}
                        onChange={handleInputChange("company_prompt")}
                        multiline
                        rows={3}
                        helperText="Auto-generated prompt based on company description, whitelist, and blacklist"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.grey[100],
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="additional_prompt"
                        label="Additional Prompt"
                        placeholder="Additional analysis prompt..."
                        value={formData.additional_prompt}
                        onChange={handleInputChange("additional_prompt")}
                        multiline
                        rows={3}
                        helperText="Additional prompt for enhanced analysis"
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.background.default,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <TextField
                          name="override_prompt"
                          label="Override Default Prompt"
                          placeholder="Enter password to unlock..."
                          value={
                            overridePromptUnlocked
                              ? formData.override_prompt
                              : ""
                          }
                          onChange={handleInputChange("override_prompt")}
                          multiline
                          rows={3}
                          helperText={
                            overridePromptUnlocked
                              ? "Override prompt that replaces the system default prompt"
                              : "Password protected - enter password to edit Default Override Prompt"
                          }
                          fullWidth
                          variant="outlined"
                          size="small"
                          disabled={!overridePromptUnlocked}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: overridePromptUnlocked
                                ? theme.palette.background.default
                                : theme.palette.grey[100],
                            },
                          }}
                        />
                        {!overridePromptUnlocked && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              type="password"
                              placeholder="Enter password"
                              value={overridePromptPassword}
                              onChange={(e) =>
                                setOverridePromptPassword(e.target.value)
                              }
                              size="small"
                              sx={{ width: "200px" }}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleOverridePromptUnlock();
                                }
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleOverridePromptUnlock}
                            >
                              Unlock
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="default_prompt"
                        label="Prompt used to evaluate headlines"
                        placeholder="Prompt will be computed from default/override prompt and company prompt..."
                        value={computeProvidedPrompt(
                          formData.override_prompt,
                          formData.company_prompt
                        )}
                        onChange={handleInputChange("default_prompt")}
                        multiline
                        rows={4}
                        helperText="Current prompt used by system (default/override prompt + company prompt) - read-only"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: theme.palette.grey[100],
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Section>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Keyword entry created successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={companyInfoSuccess}
        autoHideDuration={3000}
        onClose={() => setCompanyInfoSuccess(false)}
      >
        <Alert severity="success" onClose={() => setCompanyInfoSuccess(false)}>
          Company information loaded successfully!
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default CreateKeyword;
